import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../../components/Form';
import useRequest from '../../../../../hooks/useRequest';
import { ControlItem } from '../../../types';
import { checkIsRequired, formatParam } from '../../helpers';

import ServicePaths from '../../../../../bridge/utils/ServicePaths';

const RemoveBalanceForm = () => {
  const [userId, setUserId] = useState<string | undefined>();
  const [botId, setBotId] = useState<string | undefined>();
  const [balance, setBalance] = useState<string | undefined>();
  const [balanceType, setBalanceType] = useState<string | undefined>();

  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [canFetchBalance, setCanFetchBalance] = useState(false);
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const params = useMemo(() => ({ userId, botId }), [userId, botId]);

  const options = useMemo(
    () => ({ shouldFetch: canFetchBalance }),
    [canFetchBalance],
  );

  const { data: balanceData } = useRequest(
    balanceType === 'stable'
      ? ServicePaths.CONTROL.GetStableBalance
      : ServicePaths.CONTROL.GetAssetBalance,
    params,
    options,
  );

  useEffect(() => {
    if (balanceData) {
      setIsLoading(false);
    }
  }, [balanceData]);

  const checkIdsValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    didError = checkIsRequired(userId, setErrors, 'userId') || didError;
    didError = checkIsRequired(botId, setErrors, 'botId') || didError;

    if (typeof balanceType === 'undefined' || balanceType?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        balanceType: Validations.Required,
      }));

      didError = true;
    } else if (balanceType !== 'asset' && balanceType !== 'stable') {
      setErrors((prev) => ({
        ...prev,
        balanceType: Validations.BalanceTypeTyping,
      }));

      didError = true;
    }

    return !didError;
  }, [balanceType, botId, userId]);

  const handleGetAvailableBalanceSubmit = useCallback(() => {
    setIsLoading(true);
    const canContinue = checkIdsValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    setCanFetchBalance(true);
  }, [checkIdsValidation]);

  const checkBalanceValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    if (typeof balance === 'undefined' || balance?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        balance: Validations.Required,
      }));

      didError = true;
    } else if (typeof balance !== 'undefined') {
      const balanceValue = parseFloat(balance);

      if (isNaN(balanceValue)) {
        setErrors((prev) => ({
          ...prev,
          balance: Validations.NumberTyping,
        }));

        didError = true;
      }
    }

    return !didError;
  }, [balance]);

  const handleRemoveBalanceSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkBalanceValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url:
        balanceType === 'stable'
          ? ServicePaths.CONTROL.RemoveStableBalance
          : ServicePaths.CONTROL.RemoveAssetBalance,
      params: {
        userId: formatParam(userId),
        botId: formatParam(botId),
        balance: formatParam(balance),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [balance, balanceType, botId, checkBalanceValidation, userId]);

  const getAvailableBalanceConfig = useMemo(() => {
    return {
      title: ControlItem.RemoveBalance,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
        },
        {
          label: 'Bot Id',
          value: botId,
          handleValueChange: setBotId,
          error: errors.botId,
        },
        {
          label: 'Balance Type',
          value: balanceType,
          handleValueChange: setBalanceType,
          error: errors.balanceType,
        },
      ],
      handleSubmit: handleGetAvailableBalanceSubmit,
      isLoading,
    };
  }, [
    balanceType,
    botId,
    errors.balanceType,
    errors.botId,
    errors.userId,
    handleGetAvailableBalanceSubmit,
    isLoading,
    userId,
  ]);

  const removeBalanceConfig = useMemo(() => {
    return {
      title: ControlItem.RemoveBalance,
      formMessage: formMessage ?? balanceData,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
          isLocked: true,
        },
        {
          label: 'Bot Id',
          value: botId,
          handleValueChange: setBotId,
          error: errors.botId,
          isLocked: true,
        },
        {
          label: 'Balance Type',
          value: balanceType,
          handleValueChange: setBalanceType,
          error: errors.balanceType,
          isLocked: true,
        },
        {
          label: 'Balance',
          value: balance,
          handleValueChange: setBalance,
          error: errors.balance,
        },
      ],
      handleSubmit: handleRemoveBalanceSubmit,
      isLoading,
    };
  }, [
    balance,
    balanceData,
    balanceType,
    botId,
    errors.balance,
    errors.balanceType,
    errors.botId,
    errors.userId,
    formMessage,
    handleRemoveBalanceSubmit,
    isLoading,
    userId,
  ]);

  if (!balanceData) {
    return <Form config={getAvailableBalanceConfig} />;
  }

  return <Form config={removeBalanceConfig} />;
};

export default RemoveBalanceForm;
